import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { signOut } from "@/frontend/auth-client";

type ConsentMessageProps = {
  /** The headline. */
  title: string;
  /** One sentence below the headline. */
  children: React.ReactNode;
  /** The one action the patient can do. Some screens have no action. */
  action?: React.ReactNode;
};

/**
 * A message screen. It shows a title in the center, one line of text, and a
 * maximum of one button. Each screen that replaces the consent page has this
 * shape.
 */
export function ConsentMessage({ title, children, action }: ConsentMessageProps) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-16 text-center">
      <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 max-w-xs text-body text-muted-foreground">{children}</p>
      {action ? <div className="mt-6 w-full max-w-xs">{action}</div> : null}
    </div>
  );
}

type ConsentLoadErrorProps = {
  /** Does the acceptance lookup again. */
  onRetry: () => void;
  /** True while the retry is in progress. */
  isRetrying?: boolean;
};

/**
 * The screen for an acceptance lookup that fails. Consent is still necessary.
 * A dropped request does not let the patient go forward. But the button on this
 * screen does the lookup again, and thus the patient is not at a dead end.
 */
export function ConsentLoadError({ onRetry, isRetrying = false }: ConsentLoadErrorProps) {
  return (
    <ConsentMessage
      title="We couldn’t load this page"
      action={
        <Button className="h-11 w-full text-body" onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          Try again
        </Button>
      }
    >
      Something went wrong on our side. Your records are untouched.
    </ConsentMessage>
  );
}

/**
 * The screen for an acceptance lookup that the server refuses. The session is
 * not a patient of this workspace, or the membership behind it is gone.
 * This screen has no retry button, because a retry cannot change a 403. The
 * patient must sign out and open the link from the email again.
 */
export function ConsentBlocked() {
  return (
    <ConsentMessage
      title="This link is not open to you"
      action={
        <Button
          variant="outline"
          className="h-11 w-full text-body"
          onClick={() => signOut({ fetchOptions: { onSuccess: () => window.location.assign("/") } })}
        >
          <LogOut className="size-4" />
          Sign out
        </Button>
      }
    >
      It was set up for someone else, or it has been closed. Your records are untouched.
    </ConsentMessage>
  );
}

/**
 * The screen after the patient leaves, when there is no patient session to go
 * back to. A magic-link patient has no application that waits for them. Thus
 * setup stops here, and the text tells the patient to open the link again.
 */
export function SetupClosed() {
  return (
    <ConsentMessage title="Nothing was connected">
      You can close this tab. Open your link again whenever you want to pick up where you left off.
    </ConsentMessage>
  );
}
